import React, { useEffect, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_TRANSMISSION = {
  provider: 'youtube',
  title: '',
  stream_url: '',
  embed_url: '',
  youtube_video_id: '',
  replay_url: '',
  thumbnail_url: '',
  is_live: true,
};

function buildTransmission(match) {
  return {
    ...EMPTY_TRANSMISSION,
    ...(match?.transmission || {}),
    is_live: Boolean(match?.transmission?.is_live ?? true),
  };
}

export function TransmissionsPage({ loading, error, matches, selectedMatch, onSelectMatch, onSave, onClear, onUploadVideo, saving, uploading, canManage }) {
  const [form, setForm] = useState(EMPTY_TRANSMISSION);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoMessage, setVideoMessage] = useState('');

  useEffect(() => {
    setForm(buildTransmission(selectedMatch || null));
    setSelectedVideo(null);
    setVideoMessage('');
  }, [selectedMatch]);

  if (loading) return <LoadingBlock label="Carregando transmissões..." />;
  if (error) return <div className="page-error">{error}</div>;

  const liveCount = (matches || []).filter((item) => item.status === 'live').length;
  const withTransmission = (matches || []).filter((item) => item.transmission || item.transmission_title).length;
  const uploadSelectedVideo = async () => {
    if (!selectedVideo || !selectedMatch || !onUploadVideo) return;
    const url = await onUploadVideo(selectedVideo, selectedMatch.id);
    if (url) {
      setForm((current) => ({
        ...current,
        provider: 'upload',
        stream_url: url,
        replay_url: current.replay_url || url,
        title: current.title || `${selectedMatch.home_team_name} x ${selectedMatch.away_team_name}`,
      }));
      setSelectedVideo(null);
      setVideoMessage('Video enviado. Salve a transmissão para publicar na partida.');
    }
  };

  return (
    <div className="page-grid two-columns">
      <SectionCard title="Transmissões" subtitle="Gerencie vídeos ao vivo, embed, replay e miniatura por partida.">
        <div className="callout-box compact-box admin-callout">
          <span className="eyebrow">Admin</span>
          <strong>Controle completo dos vídeos da plataforma</strong>
          <p>Como admin, você escolhe provedor, link principal, embed, replay e miniatura. Também pode limpar a transmissão inteira de uma partida.</p>
        </div>

        <div className="callout-grid admin-summary-grid">
          <div className="callout-box compact-box"><span className="eyebrow">Jogos ao vivo</span><strong>{liveCount}</strong><p>Partidas em status ao vivo.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Com vídeo</span><strong>{withTransmission}</strong><p>Partidas com configuração de transmissão.</p></div>
        </div>

        <DataTable
          rows={matches || []}
          columns={[
            { key: 'scheduled_at', label: 'Data' },
            { key: 'home_team_name', label: 'Mandante' },
            { key: 'away_team_name', label: 'Visitante' },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'actions',
              label: 'Ação',
              render: (row) => <button className="inline-button" type="button" onClick={() => onSelectMatch(row.id)}>Gerenciar</button>,
            },
          ]}
          emptyText="Sem jogos disponíveis para transmissão."
        />
      </SectionCard>

      <SectionCard title="Operação da transmissão" subtitle="Configure vídeo ao vivo, embed, replay e miniatura.">
        {selectedMatch ? (
          <>
            <div className="callout-grid admin-summary-grid">
              <div className="callout-box compact-box"><span className="eyebrow">Partida</span><strong>{selectedMatch.home_team_name} x {selectedMatch.away_team_name}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Campeonato</span><strong>{selectedMatch.campeonato_name || '-'}</strong></div>
              <div className="callout-box compact-box"><span className="eyebrow">Ao vivo</span><strong>{form.is_live ? 'Sim' : 'Não'}</strong></div>
            </div>

            {canManage ? (
              <form className="admin-form" onSubmit={(event) => {
                event.preventDefault();
                const hasAnyValue = [form.title, form.stream_url, form.embed_url, form.youtube_video_id, form.replay_url, form.thumbnail_url]
                  .some((item) => String(item || '').trim() !== '');
                onSave(selectedMatch.id, { transmission: hasAnyValue ? {
                  provider: form.provider,
                  title: form.title || null,
                  stream_url: form.stream_url || null,
                  embed_url: form.embed_url || null,
                  youtube_video_id: form.youtube_video_id || null,
                  replay_url: form.replay_url || null,
                  thumbnail_url: form.thumbnail_url || null,
                  is_live: Boolean(form.is_live),
                } : null });
              }}>
                <div className="form-row triple">
                  <label>
                    Provedor
                    <select value={form.provider} onChange={(event) => setForm({ ...form, provider: event.target.value })}>
                      <option value="youtube">YouTube</option>
                      <option value="external">Externo</option>
                      <option value="upload">Upload</option>
                    </select>
                  </label>
                  <label>
                    Título
                    <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
                  </label>
                  <label>
                    URL principal
                    <input value={form.stream_url} onChange={(event) => setForm({ ...form, stream_url: event.target.value })} />
                  </label>
                </div>
                <div className="media-uploader">
                  <div className="media-preview video-preview">
                    {form.stream_url ? <span>Video configurado</span> : <span>Sem video</span>}
                  </div>
                  <div className="media-uploader__controls">
                    <label>
                      Video da minha maquina
                      <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(event) => setSelectedVideo(event.target.files?.[0] || null)} />
                    </label>
                    <div className="inline-actions">
                      <button className="ghost-button" type="button" onClick={uploadSelectedVideo} disabled={!selectedVideo || uploading}>
                        {uploading ? 'Enviando...' : 'Enviar video'}
                      </button>
                      {form.stream_url ? (
                        <a className="inline-button media-link" href={form.stream_url} target="_blank" rel="noreferrer">
                          Abrir video
                        </a>
                      ) : null}
                    </div>
                    <small>MP4, WEBM ou MOV até 150 MB. Para produção com muitos acessos, o ideal é storage cloud.</small>
                    {videoMessage ? <small className="success-note compact">{videoMessage}</small> : null}
                  </div>
                </div>
                <div className="form-row triple">
                  <label>
                    Embed URL
                    <input value={form.embed_url} onChange={(event) => setForm({ ...form, embed_url: event.target.value })} />
                  </label>
                  <label>
                    Replay URL
                    <input value={form.replay_url} onChange={(event) => setForm({ ...form, replay_url: event.target.value })} />
                  </label>
                  <label>
                    Miniatura URL
                    <input value={form.thumbnail_url} onChange={(event) => setForm({ ...form, thumbnail_url: event.target.value })} />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    YouTube Video ID
                    <input value={form.youtube_video_id} onChange={(event) => setForm({ ...form, youtube_video_id: event.target.value })} />
                  </label>
                  <label className="checkbox-field">
                    <span>Transmissão ao vivo</span>
                    <input type="checkbox" checked={Boolean(form.is_live)} onChange={(event) => setForm({ ...form, is_live: event.target.checked })} />
                  </label>
                </div>
                <div className="inline-actions">
                  <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar transmissão'}</button>
                  <button className="inline-button danger-text" type="button" onClick={() => onClear(selectedMatch.id)} disabled={saving}>Remover transmissão</button>
                </div>
              </form>
            ) : <div className="empty-state">Seu perfil pode consultar, mas não gerenciar transmissões.</div>}
          </>
        ) : <div className="empty-state">Selecione uma partida para gerenciar os vídeos.</div>}
      </SectionCard>
    </div>
  );
}
